import React, { useState } from "react";
import {
  useListTickets,
  useListDisputes,
  useCreateTicket,
  useCreateDispute,
  getListTicketsQueryKey,
  getListDisputesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Loader2, Headphones, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { TablePagination } from "@/components/TablePagination";

const PAGE_SIZE = 10;

export function SupportList() {
  const [ticketOpen, setTicketOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-[#0B132B]">Support</h1>

      <Tabs defaultValue="tickets" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="disputes">Disputes</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="mt-4 space-y-4">
          <Button
            onClick={() => setTicketOpen(true)}
            className="w-full bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> New ticket
          </Button>
          <TicketsTab />
        </TabsContent>

        <TabsContent value="disputes" className="mt-4 space-y-4">
          <Button
            onClick={() => setDisputeOpen(true)}
            className="w-full bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Raise dispute
          </Button>
          <DisputesTab />
        </TabsContent>
      </Tabs>

      <NewTicketDialog open={ticketOpen} onOpenChange={setTicketOpen} />
      <NewDisputeDialog open={disputeOpen} onOpenChange={setDisputeOpen} />
    </div>
  );
}

function TicketsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListTickets({
    page,
    pageSize: PAGE_SIZE,
  });
  if (isLoading)
    return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  if (error || !data)
    return <div className="text-red-500">Failed to load tickets.</div>;
  const tickets = data.items;
  if (tickets.length === 0)
    return (
      <EmptyState
        icon={Headphones}
        text="No support tickets yet. Open one if you need help."
      />
    );
  return (
    <div className="flex flex-col gap-3">
      {tickets.map((ticket) => (
        <Card key={ticket.id} className="border-gray-100 shadow-sm rounded-xl">
          <CardContent className="p-4 flex justify-between items-start gap-3">
            <div>
              <p className="font-semibold text-[15px] text-[#0B132B]">
                {ticket.subject}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {ticket.reference} • {ticket.priority} priority
              </p>
            </div>
            <StatusBadge status={ticket.status} />
          </CardContent>
        </Card>
      ))}
      <TablePagination
        page={page}
        pageSize={PAGE_SIZE}
        total={data.total}
        onPageChange={setPage}
      />
    </div>
  );
}

function DisputesTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListDisputes({
    page,
    pageSize: PAGE_SIZE,
  });
  if (isLoading)
    return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  if (error || !data)
    return <div className="text-red-500">Failed to load disputes.</div>;
  const disputes = data.items;
  if (disputes.length === 0)
    return (
      <EmptyState
        icon={AlertTriangle}
        text="No disputes raised. Raise one if a commission looks wrong."
      />
    );
  return (
    <div className="flex flex-col gap-3">
      {disputes.map((dispute) => (
        <Card key={dispute.id} className="border-gray-100 shadow-sm rounded-xl">
          <CardContent className="p-4 flex justify-between items-start gap-3">
            <div>
              <p className="font-semibold text-[15px] text-[#0B132B]">
                {dispute.subject}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{dispute.reference}</p>
              {dispute.description && (
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                  {dispute.description}
                </p>
              )}
            </div>
            <StatusBadge status={dispute.status} />
          </CardContent>
        </Card>
      ))}
      <TablePagination
        page={page}
        pageSize={PAGE_SIZE}
        total={data.total}
        onPageChange={setPage}
      />
    </div>
  );
}

function EmptyState({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center">
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
        <Icon className="w-5 h-5 text-gray-400" />
      </div>
      <p className="text-sm text-gray-500">{text}</p>
    </div>
  );
}

function NewTicketDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("medium");
  const createTicket = useCreateTicket();

  const submit = async () => {
    if (!subject.trim()) return;
    try {
      await createTicket.mutateAsync({
        data: {
          subject: subject.trim(),
          body: body.trim() || undefined,
          priority: priority as "high" | "medium" | "low",
        },
      });
      queryClient.invalidateQueries({ queryKey: getListTicketsQueryKey() });
      toast({ title: "Ticket created", description: "We'll be in touch soon." });
      setSubject("");
      setBody("");
      setPriority("medium");
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not create ticket",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">New support ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What do you need help with?"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Details (optional)</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add any details that will help us assist you"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!subject.trim() || createTicket.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createTicket.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Create ticket"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDisputeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const createDispute = useCreateDispute();

  const submit = async () => {
    if (!subject.trim()) return;
    try {
      await createDispute.mutateAsync({
        data: {
          subject: subject.trim(),
          description: description.trim() || undefined,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListDisputesQueryKey() });
      toast({
        title: "Dispute raised",
        description: "Our team will review it shortly.",
      });
      setSubject("");
      setDescription("");
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not raise dispute",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Raise a dispute</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of the issue"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Details (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what went wrong"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!subject.trim() || createDispute.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createDispute.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Submit dispute"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
